/**
 * Browser stand-in for the parts of react-native-svg that shared components
 * use. Vite aliases `react-native-svg` here; the native app uses the real
 * package. Extend it when a shared component needs another SVG element.
 */
import React from 'react';

interface SvgProps {
  width?: number | string;
  height?: number | string;
  viewBox?: string;
  preserveAspectRatio?: string;
  accessibilityLabel?: string;
  children?: React.ReactNode;
}

export default function Svg({ width, height, viewBox, preserveAspectRatio, accessibilityLabel, children }: SvgProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      style={{ display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
      role={accessibilityLabel ? 'img' : undefined}
      aria-label={accessibilityLabel}
      aria-hidden={accessibilityLabel ? undefined : true}
    >
      {children}
    </svg>
  );
}

interface PathProps {
  d: string;
  fill?: string;
  fillRule?: 'evenodd' | 'nonzero';
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
  fillOpacity?: number;
  strokeLinecap?: 'butt' | 'round' | 'square';
  strokeLinejoin?: 'miter' | 'round' | 'bevel';
}

export function Path({ d, fill, fillRule, stroke, strokeWidth, strokeOpacity, fillOpacity, strokeLinecap, strokeLinejoin }: PathProps) {
  return <path d={d} fill={fill} fillRule={fillRule} stroke={stroke} strokeWidth={strokeWidth} strokeOpacity={strokeOpacity} fillOpacity={fillOpacity} strokeLinecap={strokeLinecap} strokeLinejoin={strokeLinejoin} />;
}

interface ShapeProps {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  strokeLinecap?: 'butt' | 'round' | 'square';
  opacity?: number;
}

export function Circle({ cx, cy, r, ...paint }: ShapeProps & { cx: number; cy: number; r: number }) {
  return <circle cx={cx} cy={cy} r={r} {...paint} />;
}

export function Ellipse({ cx, cy, rx, ry, ...paint }: ShapeProps & { cx: number; cy: number; rx: number; ry: number }) {
  return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...paint} />;
}

export function Rect({ x, y, width, height, rx, ...paint }: ShapeProps & { x: number; y: number; width: number; height: number; rx?: number }) {
  return <rect x={x} y={y} width={width} height={height} rx={rx} {...paint} />;
}

export function Line({ x1, y1, x2, y2, ...paint }: ShapeProps & { x1: number; y1: number; x2: number; y2: number }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} {...paint} />;
}
